// src/components/CreateItemForm.jsx
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import CategoryAutocomplete from './CategoryAutocomplete';

const CreateItemForm = forwardRef(({
  initialName = '',
  categories = [],
  onSave,
  onCancel,
  isSaving = false,
  context = 'catalog',
  activeList
}, ref) => {
  const [formData, setFormData] = useState({
    category: '',
    price: '',
    note: ''
  });

  const [errors, setErrors] = useState({});

  // Resetear cuando cambia initialName
  useEffect(() => {
    // No reseteamos nada, solo mantenemos
  }, [initialName]);

  // Exponer método submit para que SearchOverlay pueda llamarlo
  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit();
    }
  }));

  const validateForm = () => {
    const newErrors = {};

    if (formData.price && formData.price !== '') {
      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        newErrors.price = 'Invalid price';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCategoryChange = (category) => {
    setFormData(prev => ({
      ...prev,
      category
    }));
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const dataToSave = {
        item: initialName.trim(),
        category: formData.category || 'others',
        price: formData.price ? parseFloat(formData.price).toFixed(2) : '0',
        note: formData.note || '',
        status: "0"
      };
      
      onSave(dataToSave);
    }
  };

  return (
    <div className="p-4">
      <div className="space-y-4">
        {/* Category y Price en la misma línea */}
        <div className="grid grid-cols-3 gap-4">
          {/* Category - 2/3 del espacio */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <CategoryAutocomplete
              value={formData.category}
              onChange={handleCategoryChange}
              categories={categories}
              placeholder="Select or type category..."
              disabled={isSaving}
              dropdownPosition="bottom"
            />
          </div>

          {/* Price - 1/3 del espacio */}
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                className={`w-full pl-8 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.price ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="0.00"
                disabled={isSaving}
              />
            </div>
            {errors.price && (
              <p className="text-red-500 text-xs mt-1">{errors.price}</p>
            )}
          </div>
        </div>

        {/* Note */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note (optional)
          </label>
          <textarea
            name="note"
            value={formData.note}
            onChange={handleInputChange}
            rows="4"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            placeholder="Add any notes..."
            disabled={isSaving}
          />
        </div>

        {/* Context indicator */}
        {context === 'list' && (
          <div className="bg-purple-50 p-3 rounded-lg mt-4">
            <p className="text-sm text-purple-700">
              <i className="fas fa-info-circle mr-2"></i>
              This item will be added to your <strong>{activeList?.name || 'selected'}</strong> shopping list
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

CreateItemForm.displayName = 'CreateItemForm';

export default CreateItemForm;